import { useApi } from '@repo/api'
import {
  createSessionCollection,
  type DbSession,
  useLiveQuery,
  useOpenDbSession,
} from '@repo/tanstack/db'
import { useQueryClient } from '@repo/tanstack/query'

type Api = ReturnType<typeof useApi>

const createCurrenciesCollection = (
  session: DbSession,
  api: Api,
  queryClient: ReturnType<typeof useQueryClient>,
) =>
  createSessionCollection(session, {
    getKey: currency => currency.alias,
    id: 'currencies',
    queryClient,
    queryFn: async () => {
      const currencies = await api.financials.currencies.get.query()

      // Collections are keyed, not ordered; the position keeps the order the API chose
      return currencies.map((currency, position) => ({ ...currency, position }))
    },
    queryKey: api.financials.currencies.get.queryKey(),
    schemaVersion: 1,
  })

const collections = new WeakMap<DbSession, ReturnType<typeof createCurrenciesCollection>>()

/**
 * Supported currencies are static reference data, served from the local database. The list is
 * empty until the database opens, so rate forms never suspend on it.
 */
export const useCurrencies = () => {
  const api = useApi()
  const queryClient = useQueryClient()
  const session = useOpenDbSession()

  if (session && !collections.has(session)) {
    collections.set(session, createCurrenciesCollection(session, api, queryClient))
  }

  const collection = session && collections.get(session)

  const { data } = useLiveQuery({
    query: q =>
      collection &&
      q.from({ currency: collection }).orderBy(({ currency }) => currency.position, 'asc'),
  })

  return data ?? []
}
